import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trackSchoolDetailViewed } from "@/analytics/analytics";
import {
  ExternalLink,
  Mail,
  AlertTriangle,
  GraduationCap,
  DollarSign,
  BookOpen,
  TrendingUp,
  MapPin,
  Users,
  TreePine,
  Trophy,
  Target,
  User,
  Church,
} from "lucide-react";
import { type SchoolResult, type CollegeDetail } from "@/services/schools";
import {
  formatRank,
  formatScoringAvg,
  formatCurrency,
  formatPercent,
  formatEnrollment,
  formatSATRange,
  formatACTRange,
} from "./schoolFormatters";

/* ─── Visual Components ─── */

export function SectionHeader({
  icon,
  children,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {icon && <span className="text-muted-foreground/70">{icon}</span>}
      <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
        {children}
      </h3>
    </div>
  );
}

export function StatCard({
  value,
  label,
  accent,
}: {
  value: string;
  label: string;
  accent?: boolean;
}) {
  return (
    <div
      className={
        "rounded-lg p-3 text-center " +
        (accent ? "bg-primary/5 border border-primary/10" : "bg-muted/50")
      }
    >
      <div
        className={
          "text-lg font-bold tabular-nums leading-tight " +
          (accent ? "text-primary-text" : "text-foreground")
        }
      >
        {value}
      </div>
      <div className="mt-1 text-xs font-semibold text-muted-foreground">{label}</div>
    </div>
  );
}

export function MajorBar({ name, pct }: { name: string; pct: number }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-foreground w-[140px] truncate shrink-0">{name}</span>
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary/50 rounded-full"
          style={{ width: `${Math.max(Math.min(pct, 100), 2)}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground tabular-nums w-8 text-right shrink-0">
        {pct}%
      </span>
    </div>
  );
}

export function InfoChip({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className="text-muted-foreground/60">{icon}</span>
      {children}
    </span>
  );
}

export function Badge({
  children,
  variant = "default",
}: {
  children: React.ReactNode;
  variant?: "default" | "primary";
}) {
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium " +
        (variant === "primary"
          ? "bg-primary/10 text-primary-text border border-primary/20"
          : "bg-muted text-muted-foreground")
      }
    >
      {children}
    </span>
  );
}

/* ─── School Detail Content ─── */

export function SchoolDetailContent({
  school,
  userGenderLabel,
  collegeData,
  collegeLoading,
}: {
  school: SchoolResult;
  userGenderLabel: string;
  collegeData: CollegeDetail | null;
  collegeLoading: boolean;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    if (school?.schoolName && school?.schoolId) {
      trackSchoolDetailViewed({ schoolName: school.schoolName, schoolId: school.schoolId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school?.schoolId]);

  const missingGenderTeam =
    userGenderLabel !== "All" &&
    school.availableGenders &&
    !school.availableGenders.includes(userGenderLabel);

  const admissions = collegeData?.admissions;
  const cost = collegeData?.cost;
  const outcomes = collegeData?.outcomes;

  const hasAdmissions =
    admissions &&
    (admissions.acceptance_rate != null ||
      collegeData.open_admissions != null ||
      formatSATRange(admissions) ||
      formatACTRange(admissions));

  const hasCost =
    cost &&
    (cost.tuition_in_state != null ||
      cost.tuition_out_of_state != null ||
      cost.avg_net_price != null ||
      cost.median_debt != null);

  const topPrograms = collegeData?.academics?.programs
    ?.filter(p => p.percentage > 0)
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 8);
  const hasPrograms = topPrograms && topPrograms.length > 0;

  const hasOutcomes =
    outcomes &&
    (outcomes.graduation_rate_4yr != null ||
      outcomes.graduation_rate_6yr != null ||
      outcomes.retention_rate != null ||
      outcomes.median_earnings_10yr != null);

  const hasCommitStats =
    school.commit_score_stats &&
    (school.commit_score_stats.avg_score_differential != null ||
      school.commit_score_stats.avg_class_rank != null);

  const locationStr =
    collegeData?.city && school.state
      ? `${collegeData.city}, ${school.state}`
      : school.state || null;

  return (
    <div className="space-y-5">
      {/* Info chip strip */}
      {(locationStr ||
        collegeData?.enrollment?.undergraduate != null ||
        collegeData?.locale_type ||
        collegeData?.religious_affiliation) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-2 border-y border-border/40">
          {locationStr && (
            <InfoChip icon={<MapPin className="h-3.5 w-3.5" />}>{locationStr}</InfoChip>
          )}
          {collegeData?.enrollment?.undergraduate != null && (
            <InfoChip icon={<Users className="h-3.5 w-3.5" />}>
              {formatEnrollment(collegeData.enrollment.undergraduate)} undergrad
            </InfoChip>
          )}
          {collegeData?.locale_type && (
            <InfoChip icon={<TreePine className="h-3.5 w-3.5" />}>
              {collegeData.locale_type}
            </InfoChip>
          )}
          {collegeData?.religious_affiliation && (
            <InfoChip icon={<Church className="h-3.5 w-3.5" />}>
              {collegeData.religious_affiliation}
            </InfoChip>
          )}
        </div>
      )}

      {/* Missing gender banner */}
      {missingGenderTeam && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            We don&apos;t have {userGenderLabel.toLowerCase()}&apos;s golf data for this school
          </span>
        </div>
      )}

      {/* Coach card */}
      {school.headCoach && (
        <div className="rounded-lg bg-primary/5 border border-primary/10 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <User className="h-5 w-5 text-primary-text" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">{school.headCoach}</p>
              <p className="text-xs text-muted-foreground">Head coach</p>
            </div>
            {school.headCoachEmail && (
              <button
                onClick={() =>
                  navigate("/chat", {
                    state: {
                      autoPrompt: `Draft an email to ${school.headCoach} at ${school.schoolName} (${school.headCoachEmail}).`,
                      threadTitle: `Email: ${school.schoolName}`,
                    },
                  })
                }
                className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-xs font-medium hover:bg-primary/90 transition-colors shrink-0"
              >
                <Mail className="h-3.5 w-3.5" />
                Email
              </button>
            )}
          </div>
        </div>
      )}

      {/* Team Performance */}
      <div className="space-y-2.5">
        <SectionHeader icon={<Trophy className="h-3.5 w-3.5" />}>Team Performance</SectionHeader>
        <div className="grid grid-cols-2 gap-2">
          <StatCard value={formatRank(school.rank)} label="National rank" accent />
          <StatCard value={formatScoringAvg(school.scoring_avg)} label="Scoring avg" />
        </div>
      </div>

      {/* Recent Commit Profile */}
      {hasCommitStats && (
        <div className="space-y-2.5">
          <SectionHeader icon={<Target className="h-3.5 w-3.5" />}>
            Recent Commit Profile
          </SectionHeader>
          <div className="grid grid-cols-2 gap-2">
            {school.commit_score_stats!.avg_score_differential != null && (
              <StatCard
                value={formatScoringAvg(school.commit_score_stats!.avg_score_differential)}
                label="Avg differential"
                accent
              />
            )}
            {school.commit_score_stats!.avg_class_rank != null && (
              <StatCard
                value={`#${Math.round(school.commit_score_stats!.avg_class_rank)}`}
                label="Avg class rank"
              />
            )}
          </div>
        </div>
      )}

      {/* Admissions */}
      {hasAdmissions && (
        <div className="space-y-2.5">
          <SectionHeader icon={<GraduationCap className="h-3.5 w-3.5" />}>Admissions</SectionHeader>
          <div className="grid grid-cols-3 gap-2">
            {admissions!.acceptance_rate != null && (
              <StatCard
                value={formatPercent(admissions!.acceptance_rate)!}
                label="Acceptance"
                accent
              />
            )}
            {formatSATRange(admissions) && (
              <StatCard value={formatSATRange(admissions)!} label="SAT range" />
            )}
            {formatACTRange(admissions) && (
              <StatCard value={formatACTRange(admissions)!} label="ACT range" />
            )}
          </div>
        </div>
      )}

      {/* Cost & Financial Aid */}
      {hasCost && (
        <div className="space-y-2.5">
          <SectionHeader icon={<DollarSign className="h-3.5 w-3.5" />}>
            Cost & Financial Aid
          </SectionHeader>
          <div className="grid grid-cols-2 gap-2">
            {cost!.tuition_in_state != null && (
              <StatCard value={formatCurrency(cost!.tuition_in_state)!} label="In-state tuition" />
            )}
            {cost!.tuition_out_of_state != null && (
              <StatCard value={formatCurrency(cost!.tuition_out_of_state)!} label="Out-of-state" />
            )}
            {cost!.avg_net_price != null && (
              <StatCard value={formatCurrency(cost!.avg_net_price)!} label="Avg net price" accent />
            )}
            {cost!.median_debt != null && (
              <StatCard value={formatCurrency(cost!.median_debt)!} label="Median debt" />
            )}
          </div>
          {cost!.pell_grant_rate != null && (
            <div className="flex items-center gap-3 px-1">
              <span className="text-xs text-muted-foreground">Pell Grant Rate</span>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary/50 rounded-full"
                  style={{ width: `${Math.round(cost!.pell_grant_rate! * 100)}%` }}
                />
              </div>
              <span className="text-xs font-medium text-foreground tabular-nums">
                {formatPercent(cost!.pell_grant_rate)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Top Majors */}
      {hasPrograms && (
        <div className="space-y-2.5">
          <SectionHeader icon={<BookOpen className="h-3.5 w-3.5" />}>Top Majors</SectionHeader>
          <div className="space-y-2">
            {topPrograms!.map(p => (
              <MajorBar key={p.cip_code} name={p.name} pct={Math.round(p.percentage * 100)} />
            ))}
          </div>
        </div>
      )}

      {/* Outcomes */}
      {hasOutcomes && (
        <div className="space-y-2.5">
          <SectionHeader icon={<TrendingUp className="h-3.5 w-3.5" />}>Outcomes</SectionHeader>
          <div className="grid grid-cols-2 gap-2">
            {outcomes!.graduation_rate_4yr != null && (
              <StatCard
                value={formatPercent(outcomes!.graduation_rate_4yr)!}
                label="4-year graduation"
              />
            )}
            {outcomes!.graduation_rate_6yr != null && (
              <StatCard
                value={formatPercent(outcomes!.graduation_rate_6yr)!}
                label="6-year graduation"
              />
            )}
            {outcomes!.retention_rate != null && (
              <StatCard value={formatPercent(outcomes!.retention_rate)!} label="Retention" accent />
            )}
            {outcomes!.median_earnings_10yr != null && (
              <StatCard
                value={formatCurrency(outcomes!.median_earnings_10yr)!}
                label="Earnings (10 years)"
              />
            )}
          </div>
        </div>
      )}

      {/* College loading indicator */}
      {collegeLoading && (
        <p className="text-xs text-muted-foreground italic text-center">
          Loading school details...
        </p>
      )}

      {/* Website links */}
      {(school.team_url ||
        (collegeData?.school_url && collegeData.school_url !== school.team_url)) && (
        <div className="flex flex-wrap gap-2">
          {school.team_url && (
            <a
              href={school.team_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              Team Website
            </a>
          )}
          {collegeData?.school_url && collegeData.school_url !== school.team_url && (
            <a
              href={collegeData.school_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              School Website
            </a>
          )}
        </div>
      )}
    </div>
  );
}
